import { useState } from 'react';
import { MakeTag } from '../lib/jobMakes.tsx';
import { api, fmt, fmtDate, type User } from '../api.ts';
import DataTable, { type Column } from '../components/DataTable.tsx';
import NewOrderForm from '../components/NewOrderForm.tsx';
import { useList } from '../lib/useList.ts';

/** One line of an order. The line, not the order, is the unit of work. */
interface LineRow {
  po_item_id: number;
  po_id: number;
  po_number: string | null;
  so_number: number;
  sales_order: string;
  line_no: number;
  line_ref: string;
  po_date: string;
  priority: string;
  customer_name: string;
  box_name: string;
  box_size: string | null;
  box_type: string | null;
  ups: number | null;
  quantity: string;
  unit_price: string | null;
  line_value: string;
  delivery_date: string | null;
  delivery_state: string;
  line_status: 'OPEN' | 'PART_DISPATCHED' | 'DISPATCHED' | 'CANCELLED';
}

interface LineJob {
  id: number; job_number: string; ordered_quantity: string; place: string;
  delivery_date: string | null; own_delivery_date: boolean;
}

const STATUS_LABEL: Record<LineRow['line_status'], string> = {
  OPEN: 'Open', PART_DISPATCHED: 'Part dispatched', DISPATCHED: 'Dispatched', CANCELLED: 'Cancelled',
};

export default function Orders({
  user, onOpenOrder, onOpenJob,
}: {
  user: User;
  onOpenOrder: (poId: number, poItemId?: number) => void;
  onOpenJob: (jobId: number) => void;
}) {
  const [creating, setCreating] = useState(false);
  const [open, setOpen] = useState<Set<number>>(new Set());
  const [jobs, setJobs] = useState<Map<number, LineJob[] | 'error'>>(new Map());
  const [notice, setNotice] = useState('');
  const list = useList<LineRow>('/api/purchase-orders', 'purchaseOrders',
                                { sort: 'po_date', dir: 'desc' });

  const canCreate = user.role === 'ADMIN' || user.role === 'MANAGER';

  if (creating) {
    return (
      <NewOrderForm
        onCancel={() => setCreating(false)}
        onCreated={(msg) => {
          setCreating(false); setNotice(msg); void list.reload();
        }}
      />
    );
  }

  function toggle(id: number) {
    setOpen((p) => {
      const n = new Set(p);
      if (n.has(id)) n.delete(id); else n.add(id);
      return n;
    });
    api.get<{ jobs: LineJob[] }>(`/api/po-items/${id}/jobs`)
      .then((r) => setJobs((m) => new Map(m).set(id, r.jobs)))
      .catch(() => setJobs((m) => new Map(m).set(id, 'error')));
  }

  const columns: Column<LineRow>[] = [
    {
      key: 'expand', label: '', sortable: false, filterable: false,
      render: (r) => (
        <button className="expander" aria-expanded={open.has(r.po_item_id)}
                aria-label={`${open.has(r.po_item_id) ? 'Hide' : 'Show'} jobs for ${r.line_ref}`}
                onClick={(e) => { e.stopPropagation(); toggle(r.po_item_id); }}>
          {open.has(r.po_item_id) ? '▾' : '▸'}
        </button>
      ),
    },
    {
      key: 'so_number', label: 'Order', type: 'number',
      render: (r) => (
        <button className="btn mono" style={{ padding: '2px 8px' }} title="Open the sales order"
                onClick={(e) => { e.stopPropagation(); onOpenOrder(r.po_id, r.po_item_id); }}>
          {r.line_ref}
        </button>
      ),
    },
    { key: 'customer_name', label: 'Company' },
    {
      key: 'box_name', label: 'Box',
      render: (r) => (
        <>
          {r.box_name}
          {r.box_size && <span className="dim"> · {r.box_size}</span>}
        </>
      ),
    },
    { key: 'box_type', label: 'Type', secondary: true,
      render: (r) => <span className="dim">{r.box_type ?? '—'}</span> },
    { key: 'quantity', label: 'Quantity', type: 'number', align: 'right',
      render: (r) => fmt(r.quantity) },
    { key: 'delivery_date', label: 'Delivery', type: 'date',
      render: (r) => <span className="dim">{fmtDate(r.delivery_date)}</span> },
    {
      key: 'line_status', label: 'Status', type: 'choice',
      choices: Object.entries(STATUS_LABEL).map(([value, label]) => ({ value, label })),
      render: (r) => (
        <span style={{ whiteSpace: 'nowrap' }}>
          {(r.priority === 'URGENT' || r.priority === 'HIGH') && r.line_status !== 'DISPATCHED'
            && r.line_status !== 'CANCELLED' && (
            <span className={`tag ${r.priority === 'URGENT' ? 'urgent' : 'high'}`} style={{ marginRight: 6 }}>
              {r.priority === 'URGENT' ? 'Urgent' : 'High'}
            </span>
          )}
          <span className={r.line_status === 'OPEN' || r.line_status === 'PART_DISPATCHED' ? undefined : 'dim'}>
            {STATUS_LABEL[r.line_status]}
          </span>
        </span>
      ),
    },
  ];

  return (
    <div className="panel">
      {notice && <div style={{ margin: 16 }} className="tag ready notice">{notice}</div>}
      <DataTable
        list={list}
        columns={columns}
        exportName="/api/purchase-orders"
        rowKey={(r) => String(r.po_item_id)}
        rowClass={(r) => r.delivery_state === 'LATE' ? 'late'
                       : r.delivery_state === 'DUE_SOON' ? 'soon' : ''}
        onRowClick={(r) => onOpenOrder(r.po_id, r.po_item_id)}
        below={(r) => {
          if (!open.has(r.po_item_id)) return null;
          const list = jobs.get(r.po_item_id);
          const cls = (c: Column<LineRow>) => [c.align === 'right' ? 'num' : '', c.secondary ? 'col-secondary' : '']
            .filter(Boolean).join(' ');
          if (list === undefined || list === 'error' || list.length === 0) {
            return (
              <tr className="sub-row">
                <td />
                <td colSpan={columns.length - 1} className="dim">
                  {list === undefined ? 'Loading…' : list === 'error' ? 'Could not load the jobs' : 'No job card yet'}
                </td>
              </tr>
            );
          }
          return list.map((j) => (
            <tr key={j.id} className="sub-row">
              {columns.map((c) => (
                <td key={c.key} className={cls(c)}>
                  {c.key === 'so_number' ? (
                    <>
                      <button className="linkish mono sub-job" title="Open the job card"
                              onClick={() => onOpenJob(j.id)}>
                        {j.job_number}
                      </button>
                      <MakeTag number={j.job_number} />
                    </>
                  ) : c.key === 'quantity' ? fmt(j.ordered_quantity)
                    : c.key === 'delivery_date' ? (
                      j.own_delivery_date && <span className="dim">{fmtDate(j.delivery_date)} · own</span>
                    ) : c.key === 'line_status' ? <span className="dim">{j.place}</span> : null}
                </td>
              ))}
            </tr>
          ));
        }}
        empty={`No orders yet.${canCreate ? ' Use “New sales order” to add the first one.' : ''}`}
        toolbar={canCreate && (
          <button className="btn primary" onClick={() => { setNotice(''); setCreating(true); }}>
            New sales order
          </button>
        )}
      />
    </div>
  );
}
